import React, { useState } from 'react';

import { useUpdateEffect } from 'react-use';
import { useIntl } from 'react-intl';
import { Form, ToggleButtonGroup, ToggleButton, Col, Row } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { RRule, rrulestr } from 'rrule';
import { toNumber } from 'lodash';

import { Dropdown } from 'components/Forms';
import { isLegacyRecurrence, RecurrenceValue } from 'utils/RecurrenceEngine';
import { generateUUID } from 'utils/Helpers/UUID';

interface IRecurrenceEngineProps {
  recurrence: string;
  isActive: boolean;
  frequency?: string | null;
  onChange: (recurrence: string, valid: boolean, frequency: string | null) => any;
  date: DateTime;
  legacyOptions?: boolean;
}

interface ISelectOptionWithIndex extends ISelectOption {
  index: number;
}

const RecurrenceEngine = ({
  recurrence,
  frequency,
  isActive,
  onChange,
  date,
  legacyOptions = true
}: IRecurrenceEngineProps) => {
  const intl = useIntl();

  const legacyPeriodOptions: ISelectOption[] = [
    { label: intl.formatMessage({ id: 'dailyKey' }), value: RecurrenceValue.DAILY },
    { label: intl.formatMessage({ id: 'weeklyKey' }), value: RecurrenceValue.WEEKLY },
    { label: intl.formatMessage({ id: 'monthlyKey' }), value: RecurrenceValue.MONTHLY }
  ];

  const fullPeriodOptions: ISelectOption[] = (
    [
      {
        label: intl.formatMessage({ id: RecurrenceValue.ONE_TIME }),
        value: RecurrenceValue.ONE_TIME
      }
      // DEBUG only - not available for PROD
      // {
      //   label: intl.formatMessage({ id: RecurrenceValue.MINUTELY }),
      //   value: RecurrenceValue.MINUTELY
      // }
      // {
      //   label: intl.formatMessage({ id: 'everyMorningAt9amKey' }),
      //   value: RecurrenceValue.EVERY_MORNING_9_AM,
      // },
      // {
      //   label: intl.formatMessage({ id: RecurrenceValue.ONCE_WEEKLY_9_AM }),
      //   value: RecurrenceValue.ONCE_WEEKLY_9_AM,
      // },
      // {
      //   label: intl.formatMessage({ id: RecurrenceValue.ONCE_EVERY_OTHER_WEEK_9_AM }),
      //   value: RecurrenceValue.ONCE_EVERY_OTHER_WEEK_9_AM,
      // },
      // {
      //   label: intl.formatMessage({ id: RecurrenceValue.ONCE_MONTHLY }),
      //   value: RecurrenceValue.ONCE_MONTHLY,
      // },
      // {
      //   label: intl.formatMessage({ id: RecurrenceValue.AS_DIRECTED }),
      //   value: RecurrenceValue.AS_DIRECTED,
      // },
      // {
      //   label: intl.formatMessage({ id: RecurrenceValue.AS_NEEDED }),
      //   value: RecurrenceValue.AS_NEEDED,
      // },
    ] as ISelectOption[]
  ).concat(legacyPeriodOptions);

  const weekdays: ISelectOptionWithIndex[] = [
    { label: intl.formatMessage({ id: 'MondayLong' }), value: 'MO', index: 0 },
    { label: intl.formatMessage({ id: 'TuesdayLong' }), value: 'TU', index: 1 },
    { label: intl.formatMessage({ id: 'WednesdayLong' }), value: 'WE', index: 2 },
    { label: intl.formatMessage({ id: 'ThursdayLong' }), value: 'TH', index: 3 },
    { label: intl.formatMessage({ id: 'FridayLong' }), value: 'FR', index: 4 },
    { label: intl.formatMessage({ id: 'SaturdayLong' }), value: 'SA', index: 5 },
    { label: intl.formatMessage({ id: 'SundayLong' }), value: 'SU', index: 6 },
    { label: intl.formatMessage({ id: 'dayKey' }), value: 'DA', index: -1 }
  ];

  const weekdaysShort: ISelectOptionWithIndex[] = [
    { label: intl.formatMessage({ id: 'MondayShort' }), value: 'MO', index: 0 },
    { label: intl.formatMessage({ id: 'TuesdayShort' }), value: 'TU', index: 1 },
    { label: intl.formatMessage({ id: 'WednesdayShort' }), value: 'WE', index: 2 },
    { label: intl.formatMessage({ id: 'ThursdayShort' }), value: 'TH', index: 3 },
    { label: intl.formatMessage({ id: 'FridayShort' }), value: 'FR', index: 4 },
    { label: intl.formatMessage({ id: 'SaturdayShort' }), value: 'SA', index: 5 },
    { label: intl.formatMessage({ id: 'SundayShort' }), value: 'SU', index: 6 }
  ];

  const monthlyPositions: ISelectOptionWithIndex[] = [
    { label: intl.formatMessage({ id: 'firstKey' }), value: '1', index: 1 },
    { label: intl.formatMessage({ id: 'secondKey' }), value: '2', index: 2 },
    { label: intl.formatMessage({ id: 'thirdKey' }), value: '3', index: 3 },
    { label: intl.formatMessage({ id: 'fourthKey' }), value: '4', index: 4 },
    { label: intl.formatMessage({ id: 'lastKey' }), value: '-1', index: -1 }
  ];

  const monthDays: ISelectOption[] = [];
  for (let i = 1; i <= 31; i++) {
    monthDays[i] = { value: `${i}`, label: `${i}` };
  }

  const getFreqStrFromRuleIndex = (value?: number): RecurrenceValue | null => {
    switch (value) {
      case 1:
        return RecurrenceValue.MONTHLY;
      case 2:
        return RecurrenceValue.WEEKLY;
      case 3:
        return RecurrenceValue.DAILY;
      default:
        return null;
    }
  };

  const getStaticRecurrency = () => {
    switch (recurrenceFrequency) {
      case RecurrenceValue.EVERY_MORNING_9_AM:
      case RecurrenceValue.ONCE_WEEKLY_9_AM:
      case RecurrenceValue.ONCE_MONTHLY:
        return 1;
      case RecurrenceValue.ONCE_EVERY_OTHER_WEEK_9_AM:
        return 2;
      default:
        return null;
    }
  };

  const rule = recurrence ? RRule.fromString(recurrence) : null;
  const days = rule?.options.byweekday || [];
  const daysMonthPrecise = (rule?.options.bynweekday && rule?.options.bynweekday[0]) || [];
  const numbersInMonth = rule?.options.bymonthday || [DateTime.now().day];

  const lastDay = rule?.options.bynmonthday || [];
  const recurrenceRuleFrequencyValue =
    (frequency as RecurrenceValue | null) || getFreqStrFromRuleIndex(rule?.options.freq);
  const recurrenceIntervalValue =
    rule?.options.interval || rule?.options.interval === 0
      ? rule?.options.interval.toString()
      : '1';

  const [recurrenceActive, setRecurrenceActive] = useState(isActive);
  const [recurrenceFrequency, setRecurrenceFrequency] = useState<RecurrenceValue | null>(
    recurrenceRuleFrequencyValue
  );
  const [recurrentEvery, setRecurrentEvery] = useState<string>(recurrenceIntervalValue);
  const recurrentEveryNumber = toNumber(recurrentEvery);
  const invalidRecurrenceInterval =
    !recurrentEveryNumber || recurrentEveryNumber <= 0 || recurrentEveryNumber >= 366;
  const [weekDaysSelected, setWeekDaysSelected] = useState<string[]>(
    days.map((day) => weekdaysShort.find((e) => e.index === day)?.value || '')
  );
  const [weekNumbersInMonthSelected, setWeekNumbersInMonthSelected] = useState<string[]>(
    numbersInMonth.map((e) => e.toString())
  );

  const isLegacyOption = isLegacyRecurrence(recurrenceFrequency);
  const isDailyOption = (frequency: RecurrenceValue | null = recurrenceFrequency) => {
    return (
      frequency && [RecurrenceValue.DAILY, RecurrenceValue.EVERY_MORNING_9_AM].includes(frequency)
    );
  };
  const isWeeklyOption = (frequency: RecurrenceValue | null = recurrenceFrequency) => {
    return (
      frequency &&
      [
        RecurrenceValue.WEEKLY,
        RecurrenceValue.ONCE_WEEKLY_9_AM,
        RecurrenceValue.ONCE_EVERY_OTHER_WEEK_9_AM
      ].includes(frequency)
    );
  };
  const isMonthlyOption = (frequency: RecurrenceValue | null = recurrenceFrequency) => {
    return frequency && [RecurrenceValue.MONTHLY, RecurrenceValue.ONCE_MONTHLY].includes(frequency);
  };
  const isNoRecurrenceOptions =
    recurrenceFrequency &&
    [RecurrenceValue.AS_DIRECTED, RecurrenceValue.AS_NEEDED].includes(recurrenceFrequency);
  // custom picker options are options that uses the daily/weekly/monthly picker
  const isCustomPickerOption =
    isLegacyOption ||
    isDailyOption() ||
    isWeeklyOption() ||
    isMonthlyOption() ||
    recurrenceFrequency === RecurrenceValue.MINUTELY;

  let monthMethodSaved = '';
  let precisePositionSaved: string[] = [];
  let preciseDaySaved: string[] = [];
  if (isMonthlyOption()) {
    if (daysMonthPrecise?.length) {
      monthMethodSaved = 'PRECISE';
      const value1 = monthlyPositions.find((e) => e.index === daysMonthPrecise[1])?.value;
      precisePositionSaved = value1 ? [value1] : [];
      const value2 = weekdays.find((e) => e.index === daysMonthPrecise[0])?.value;
      preciseDaySaved = value2 ? [value2] : [];
    } else if (lastDay[0] === -1) {
      monthMethodSaved = 'PRECISE';
      precisePositionSaved = ['-1'];
      preciseDaySaved = ['DA'];
    } else if (numbersInMonth.length) {
      monthMethodSaved = 'DAYS';
    }
  }

  const [monthMethod, setMonthMethod] = useState(monthMethodSaved);
  const [monthlyPrecisePosition, setMonthlyPrecisePosition] = useState(
    precisePositionSaved[0] || ''
  );
  const [monthlyPreciseDay, setMonthlyPreciseDay] = useState(preciseDaySaved[0] || '');

  const getEveryLabelKey = () => {
    if (isDailyOption()) return 'daysKey';
    else if (isWeeklyOption()) return 'weeksKey';
    else if (isMonthlyOption()) return 'monthsKey';
    else return 'minutesKey';
  };

  const getFreqStrFromRecurrenceValue = () => {
    if (!recurrenceFrequency) return '';
    else if (isWeeklyOption()) return RecurrenceValue.WEEKLY;
    else if (isMonthlyOption()) return RecurrenceValue.MONTHLY;
    else if (recurrenceFrequency === RecurrenceValue.MINUTELY) return RecurrenceValue.MINUTELY;
    return RecurrenceValue.DAILY;
  };

  const onChangeByType = (type: string, value?: string | string[]) => {
    if (type === 'switchRecurrentEvent') {
      if ((value === 'yes' && recurrenceActive) || (value === 'no' && !recurrenceActive)) {
        return;
      }
      const newValue = !recurrenceActive;
      setRecurrenceActive(newValue);
      if (newValue && !recurrenceFrequency) {
        setRecurrenceFrequency(RecurrenceValue.DAILY);
      }
    }
    if (type === 'recurrentFrequency' && value) {
      setRecurrenceFrequency(value as RecurrenceValue);
      if (isMonthlyOption(value as RecurrenceValue)) {
        setMonthMethod(monthMethod || 'DAYS');
      }
      if (isWeeklyOption(value as RecurrenceValue) && weekDaysSelected.length === 0) {
        const dayIndex = parseInt(DateTime.now().toFormat('E'));
        setWeekDaysSelected([weekdaysShort[dayIndex - 1].value]);
      }
    } else if (type === 'recurrentEvery') {
      setRecurrentEvery(value as string);
    } else if (type === 'weekDaysSelected') {
      const values =
        weekdaysShort.filter((elem) => value?.includes(elem.value))?.map((e) => e.value) || [];
      setWeekDaysSelected(values);
    } else if (type === 'weekNumbersInMonthSelected') {
      const values =
        monthDays.filter((elem) => value?.includes(elem.value))?.map((e) => e.value) || [];
      setWeekNumbersInMonthSelected(values);
    } else if (type === 'monthlyPrecisePosition') {
      setMonthlyPrecisePosition(value as string);
    } else if (type === 'monthlyPreciseDay') {
      setMonthlyPreciseDay(value as string);
    } else if (type === 'monthMethod') {
      setMonthMethod(value as string);
      if (value === 'PRECISE') {
        !monthlyPreciseDay && setMonthlyPreciseDay(weekdays[0].value);
        !monthlyPrecisePosition && setMonthlyPrecisePosition(monthlyPositions[0].value);
      }
    }
  };

  const getRecurrenceString = (): string => {
    if (!recurrenceActive || !recurrenceFrequency || isNoRecurrenceOptions) {
      return '';
    }
    const freqString = `FREQ=${getFreqStrFromRecurrenceValue()}`;
    const intervalString = `;INTERVAL=${getStaticRecurrency() || recurrentEvery}`;
    let weekDayString =
      isWeeklyOption() && weekDaysSelected?.length ? `;BYDAY=${weekDaysSelected}` : '';
    let monthDayString =
      isMonthlyOption() && weekNumbersInMonthSelected?.length
        ? `;BYMONTHDAY=${weekNumbersInMonthSelected}`
        : '';
    // const dateString =
    //   legacyOptions || isLegacyOption
    //     ? `BYHOUR=${date.hour};BYMINUTE=${date.minute};BYSECOND=${date.second}`
    //     : 'BYHOUR=9;BYMINUTE=0;BYSECOND=0';
    const dateString =
      recurrenceFrequency !== RecurrenceValue.MINUTELY
        ? `;BYHOUR=${date.hour};BYMINUTE=${date.minute};BYSECOND=${date.second}`
        : '';
    const countString = recurrenceFrequency === RecurrenceValue.ONE_TIME ? ';COUNT=1' : '';
    if (isDailyOption()) {
      weekDayString = '';
      monthDayString = '';
    } else if (isWeeklyOption()) {
      monthDayString = '';
    } else if (isMonthlyOption()) {
      weekDayString = '';
      if (monthMethod === 'PRECISE') {
        if (monthlyPreciseDay === 'DA') {
          if (monthlyPrecisePosition === '-1') {
            weekDayString = '';
            monthDayString = ';BYMONTHDAY=-1';
          } else if (monthlyPrecisePosition === '1') {
            weekDayString = '';
            monthDayString = ';BYMONTHDAY=1';
          } else {
            weekDayString = '';
            monthDayString = `;BYMONTHDAY=${monthlyPrecisePosition}`;
          }
        } else {
          weekDayString = `;BYDAY=${monthlyPrecisePosition}${monthlyPreciseDay}`;
          monthDayString = '';
        }
      }
    }

    const result = `${freqString}${intervalString}${countString}${monthDayString}${weekDayString}${dateString}`;
    // Validate the rrule string
    // if it's invalid, the page will freeze but at least we're not sending it
    // to the backend which might have trouble with it too
    const rruleObj = rrulestr(result);
    rruleObj.after(new Date());
    return result;
  };

  useUpdateEffect(() => {
    let valid = !recurrenceActive;
    if (recurrenceActive) {
      if (isWeeklyOption()) {
        // need to select at least 1 day for the recurrence to be valid.
        valid = weekDaysSelected.length >= 1;
      } else if (isMonthlyOption()) {
        // if on specific days, needs to be at least one. If using 'precise' option: always valid.
        valid = monthMethod === 'DAYS' ? weekNumbersInMonthSelected.length >= 1 : true;
      } else if (recurrenceFrequency) {
        valid = true;
      }

      if (invalidRecurrenceInterval) {
        valid = false;
      }
    }
    onChange(getRecurrenceString(), valid, recurrenceFrequency);
  }, [
    recurrenceActive,
    monthMethod,
    monthlyPrecisePosition,
    monthlyPreciseDay,
    recurrenceFrequency,
    recurrentEvery,
    weekDaysSelected,
    weekNumbersInMonthSelected,
    date
  ]);

  useUpdateEffect(() => {
    if (recurrence !== getRecurrenceString()) {
      setRecurrenceFrequency(null);
    }
  }, [recurrence]);

  return (
    <div className={`${legacyOptions ? 'mt-4' : ''}`}>
      {legacyOptions && (
        <div className="mb-2 d-flex align-items-center">
          <Form.Label className="m-0">
            {intl.formatMessage({ id: 'recurrentEventLabelKey' })}
          </Form.Label>
          <ToggleButtonGroup
            data-test="recurrent-event"
            type="checkbox"
            size="sm"
            className="ms-2 z-0"
            value={[recurrenceActive ? 'yes' : 'no']}
          >
            <ToggleButton
              id={generateUUID()}
              data-test="recurrent-event-yes"
              value="yes"
              variant="outline-primary"
              onClick={() => onChangeByType('switchRecurrentEvent', 'yes')}
            >
              {intl.formatMessage({ id: 'yesKey' })}
            </ToggleButton>
            <ToggleButton
              id={generateUUID()}
              data-test="recurrent-event-no"
              value="no"
              variant="outline-primary"
              onClick={() => onChangeByType('switchRecurrentEvent', 'no')}
            >
              {intl.formatMessage({ id: 'noKey' })}
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      )}
      {recurrenceActive && (
        <div className={`${legacyOptions ? 'ms-2' : ''} d-flex flex-column`}>
          <Row>
            <Form.Group as={Col} className={`form group ${!isCustomPickerOption ? 'mb-0' : ''}`}>
              <Dropdown
                variant="form"
                dataTestId="recurrent-event-frequency"
                label={intl.formatMessage({ id: 'frequencyLabelKey' })}
                options={legacyOptions ? legacyPeriodOptions : fullPeriodOptions}
                onChange={(value: ISelectOption) =>
                  onChangeByType('recurrentFrequency', value.value)
                }
                value={fullPeriodOptions.find((opt) => opt.value === recurrenceFrequency)}
                required
              />
            </Form.Group>
          </Row>
          {isCustomPickerOption && (
            <Row>
              <Form.Group
                as={Col}
                className={`form-group ${
                  isDailyOption() || recurrenceFrequency === RecurrenceValue.MINUTELY ? 'mb-0' : ''
                }`}
              >
                <Form.Label>{intl.formatMessage({ id: 'frequencyEveryKey' })}</Form.Label>
                <div className="d-flex align-items-center">
                  <Form.Control
                    data-test="recurrent-event-frequency-every"
                    size="sm"
                    type="number"
                    className="w-40 me-2"
                    defaultValue={getStaticRecurrency() || recurrentEvery}
                    onBlur={(e: any) => onChangeByType('recurrentEvery', e.target.value)}
                    min={1}
                    isInvalid={invalidRecurrenceInterval}
                    disabled={!isLegacyOption && recurrenceFrequency !== RecurrenceValue.MINUTELY}
                  />
                  <Form.Label className="m-0">
                    {intl.formatMessage({ id: getEveryLabelKey() })}
                  </Form.Label>
                </div>
              </Form.Group>
            </Row>
          )}
          <div>
            {isWeeklyOption() && (
              <Form.Group className="form-group d-flex flex-column mb-0">
                <Form.Label>{intl.formatMessage({ id: 'whichDaysOfTheWeekLabelKey' })}</Form.Label>
                <ToggleButtonGroup
                  data-test="recurrent-event-weekdays"
                  type="checkbox"
                  size="sm"
                  className="z-0"
                  value={weekDaysSelected}
                  onChange={(elems) => onChangeByType('weekDaysSelected', elems)}
                >
                  {weekdaysShort.map((day, index) => (
                    <ToggleButton
                      id={generateUUID()}
                      data-test={`recurrent-event-weekday-${index}`}
                      key={index}
                      value={day.value}
                      variant="light"
                      className={`${
                        weekDaysSelected.includes(day.value) ? 'btn-outline-primary' : ''
                      }`}
                    >
                      {day.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Form.Group>
            )}
            {isMonthlyOption() && (
              <Form.Group className="form-group d-flex flex-column mb-0">
                <Form.Check
                  data-test="recurrent-event-monthly-days"
                  type="checkbox"
                  checked={monthMethod === 'DAYS'}
                  onChange={() => onChangeByType('monthMethod', 'DAYS')}
                  label={intl.formatMessage({ id: 'whichDaysOfTheMonthLabelKey' })}
                />
                <ToggleButtonGroup
                  data-test="recurrent-event-monthly-days"
                  className="d-inline w-50 z-0 mt-2"
                  type="checkbox"
                  size="sm"
                  value={weekNumbersInMonthSelected}
                  onChange={(elems) => onChangeByType('weekNumbersInMonthSelected', elems)}
                >
                  {monthDays.map((day, index) => (
                    <ToggleButton
                      id={generateUUID()}
                      data-test={`recurrent-event-monthly-day-${day.value}`}
                      key={index}
                      value={day.value}
                      variant="light"
                      className={`w-25 ${
                        weekNumbersInMonthSelected.includes(day.value) ? 'btn-outline-primary' : ''
                      }`}
                    >
                      {day.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <div className="mt-3 d-flex align-items-center">
                  <Form.Check
                    data-test="recurrent-event-monthly-precise"
                    type="checkbox"
                    checked={monthMethod === 'PRECISE'}
                    onChange={() => onChangeByType('monthMethod', 'PRECISE')}
                    label={intl.formatMessage({ id: 'orBePreciseKey' })}
                  />

                  <Dropdown
                    dataTestId="recurrent-event-monthly-precise-position"
                    variant="form"
                    className="ms-2 w-175"
                    options={monthlyPositions}
                    value={monthlyPositions.find((elem) => elem.value === monthlyPrecisePosition)}
                    onChange={({ value }: ISelectOption) =>
                      onChangeByType('monthlyPrecisePosition', value)
                    }
                  />
                  <Dropdown
                    dataTestId="recurrent-event-monthly-precise-day"
                    variant="form"
                    className="ms-2 w-175"
                    options={weekdays}
                    value={weekdays.find((elem) => elem.value === monthlyPreciseDay)}
                    onChange={({ value }: ISelectOption) =>
                      onChangeByType('monthlyPreciseDay', value)
                    }
                    containerNoWrap
                  />
                </div>
              </Form.Group>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RecurrenceEngine;
